import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/actions';
import {
    DEFAULT_STATUS_FOR_ARCHIVED_SEARCH,
    DEFAULT_STATUS_FOR_ONLINE_SEARCH,
    getBuyNowFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '@/utils/queryParams';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { fetchSearchSuggestions, getSearchSuggestions } from '@/redux/modules/searchSuggestions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCurrentUrlFromState } from '@/redux/modules/route';
import { getIsWhiteLabel, getWhiteLabelSellerId } from '@/redux/modules/whiteLabel';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchUrlWithParams, getSellerUrl } from '@/utils/urls';
import { injectIntl, IntlShape } from 'react-intl';
import { isHeaderDropdownVisible } from '@/redux/modules/headerSearchDropdown';
import { Location } from 'history';
import { push } from 'connected-react-router';
import { updateSearchInputBuffer } from '@/redux/modules/search';
import Autosuggest from 'react-autosuggest';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import classNames from 'classnames';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import debounce from 'lodash/debounce';
import escapeRegExp from 'lodash/escapeRegExp';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '@/utils/noop';
import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import styles from './SearchInput.module.scss';

export const enterKeyCode = 13;
export const tabKeyCode = 9;

const mapStateToProps = (state, { location }) => {
    const { pathname, search } = location;
    const pageSize = getSearchPageSize(state);
    const queryParams: any = parseQueryParams(search);
    const isBuyNow = getBuyNowFromQueryParams(queryParams);
    const priceGuide = pathname.includes('/price-guide/');
    const archivedSearchGlobalState = isArchivedSearch(queryParams, pathname) && !isBuyNow && !priceGuide;
    const currentUrl = getCurrentUrlFromState(state);
    const isOnSearchPage = currentUrl.includes('/search');
    const dropdownVisible = isOnSearchPage && archivedSearchGlobalState && isHeaderDropdownVisible(state);
    const suggestions = getSearchSuggestions(state);
    const whiteLabel = getIsWhiteLabel(state);
    const sellerId = getWhiteLabelSellerId(state);
    const { keyword = '' } = queryParams;

    const cleanSearchString = cleanSearchTerms(keyword);

    return {
        archivedSearchGlobalState,
        cleanSearchString,
        currentUrl,
        dropdownVisible,
        pageSize,
        pathname,
        sellerId,
        suggestions,
        whiteLabel,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchSearchSuggestions,
            push,
            updateSearchInputBuffer,
        },
        dispatch
    );

type Props = {
    archivedSearchGlobalState?: boolean;
    className?: string;
    cleanSearchString: string;
    currentUrl: string;
    dropdownVisible?: boolean;
    fetchSearchSuggestions: Function;
    forceArchiveOnly?: boolean;
    id: string;
    intl: IntlShape;
    isBuyNow?: boolean;
    location: Location;
    name: string;
    pageSize: number;
    push: Function;
    reverseButton?: boolean;
    sellerId: number;
    suggestions: any[];
    updateSearchInputBuffer: Function;
    whiteLabel?: boolean;
};

type State = {
    archiveSearchStatus: boolean;
    dropdownVisible: boolean;
    text: string;
};

export class SearchInput extends Component<Props, State> {
    static defaultProps = {
        className: '',
        suggestions: [],
    };

    constructor(props: Props) {
        super(props);
        this.getSuggestions = debounce(this.getSuggestions, 250);
    }

    state = {
        archiveSearchStatus: this.props.archivedSearchGlobalState,
        dropdownVisible: this.props.archivedSearchGlobalState,
        text: '',
    };

    componentDidMount() {
        this.timeout = setTimeout(() => {
            const inputElement = document.querySelector(`[aria-controls="react-autowhatever-${this.props.id}"]`);
            // @ts-ignore
            if (inputElement?.value && !this.state.text) {
                // @ts-ignore
                this.setState({ text: inputElement.value });
            }
        }, 1000);
    }

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.dropdownVisible && this.props.dropdownVisible && this.props.archivedSearchGlobalState) {
            this.setState({ archiveSearchStatus: true });
        } else if (prevProps.archivedSearchGlobalState !== this.props.archivedSearchGlobalState) {
            this.setState({ archiveSearchStatus: this.props.archivedSearchGlobalState });
        }

        // checking prop changes to ensure that the dropdown is still visible when you select upcoming
        if ((this.state.archiveSearchStatus && !this.props.dropdownVisible) || this.props.dropdownVisible) {
            this.setState({ dropdownVisible: true });
        }

        if (this.props.cleanSearchString !== prevProps.cleanSearchString) {
            this.setState({
                text: this.props.cleanSearchString,
            });
        }
    }

    timeout: any;

    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKeyCode || event.keyCode === tabKeyCode) {
            this.search();
            // @ts-ignore
            event.target.blur();
        }
    };

    getSuggestionValue = (suggestion: any) => {
        return suggestion ? suggestion.value : '';
    };
    getSectionSuggestions = (suggestion: any) => suggestion.suggestions;

    searchPhrase = (keyword: string) => {
        const searchStatus =
            this.state.archiveSearchStatus || this.props.forceArchiveOnly
                ? DEFAULT_STATUS_FOR_ARCHIVED_SEARCH
                : DEFAULT_STATUS_FOR_ONLINE_SEARCH;

        const params: any = {
            keyword,
            sort: DEFAULT_SORT_ORDER,
            status: searchStatus,
        };

        if (this.props.whiteLabel) {
            params.house_id = `${this.props.sellerId}`;
        }

        this.props.updateSearchInputBuffer(keyword);
        const search = getSearchUrlWithParams(params);

        this.props.push(search);
    };

    searchHouse = (houseId: number, houseName: string) => {
        this.props.push({
            pathname: getSellerUrl(houseId, houseName),
        });
    };

    search = (suggestion?: any) => {
        const { text } = this.state;

        const { suggestions } = this.props;
        if (suggestion && suggestion.value) {
            if (suggestion.houseId) {
                this.searchHouse(suggestion.houseId, suggestion.value);
            } else {
                this.searchPhrase(suggestion.value);
            }
        } else {
            if (text.trim().length === 0) {
                return;
            }
            if (suggestions && suggestions[1]) {
                let suggestedHouse = suggestions[1].suggestions.find((suggestion) => suggestion.value === text.trim());
                if (suggestedHouse) {
                    this.searchHouse(suggestedHouse.houseId, suggestedHouse.value);
                } else {
                    // We replace the capital OR's to prevent them from being assumed to be added by algolia
                    this.searchPhrase(text.trim().replace(/ OR /g, ' or '));
                }
            } else {
                this.searchPhrase(text.trim().replace(/ OR /g, ' or '));
            }
        }
    };

    handleInputChange = (event: any, { newValue }: any) => this.setState({ text: newValue });
    getSuggestions = (query: any) => this.props.fetchSearchSuggestions(query);

    changeSearchStatus = ({ target }: any) => {
        this.setState({ archiveSearchStatus: target?.value === 'sold' });
    };

    renderSuggestion = (suggestion: any, { query }: any) => {
        return (
            <Link onClick={() => this.search(suggestion)}>
                <span
                    className={styles['suggestions-result']}
                    dangerouslySetInnerHTML={{
                        __html: suggestion.label.replace(
                            new RegExp(`(${escapeRegExp(query)})`, 'gi'),
                            `<span class="${styles['query-match']}">$1</span>`
                        ),
                    }}
                />
            </Link>
        );
    };

    renderSectionTitle = (section: any) => {
        if (section.title && section.suggestions.length > 0) {
            return (
                <div className={styles['auction-house-wrapper']}>
                    <span className={styles['auction-house-divider']}>{section.title}</span>
                    <span className={styles['auction-house-line']} />
                </div>
            );
        }
    };

    onSuggestionsFetchRequested = ({ value }: any) => {
        this.getSuggestions(value);
    };

    shouldRenderSuggestions = (value: any) => {
        if (value) {
            if (value.value) {
                return value.value.trim().length > 0;
            }
            return value.trim().length > 0;
        }
    };

    render() {
        const { className = '', id, intl, reverseButton, suggestions, whiteLabel } = this.props;

        const { archiveSearchStatus, dropdownVisible, text } = this.state;

        const { formatMessage } = intl;

        const classes = classNames(styles['search-input'], className);
        const inputClasses = classNames(styles['search-dropdown'], {
            [styles.sold]: archiveSearchStatus,
            [styles.mobile]: reverseButton,
        });

        const placeholderId = archiveSearchStatus
            ? 'search_input_search_past_auction_price_results'
            : whiteLabel
            ? 'search_input_search_for_items_and_auction_houses_white_label'
            : 'search_input_search_for_items_and_auction_houses';

        const inputProps = {
            maxLength: 500, // Algolia has a hard limit of 512 chars.  we might need some space for escaping things
            onChange: this.handleInputChange,
            onKeyDown: this.onKeyDown,
            placeholder: formatMessage({
                id: placeholderId,
            }),
            value: text,
        };

        const iconStyles = classNames(styles.iconWrapper, { [styles.reversed]: reverseButton });
        return (
            <div className={classes}>
                {dropdownVisible && (
                    <>
                        <div className={inputClasses}>
                            <Input
                                id="choose-search"
                                onChange={this.changeSearchStatus}
                                type="select"
                                value={archiveSearchStatus ? 'sold' : 'upcoming'}
                            >
                                <option key="sold" value="sold">
                                    {formatMessage({
                                        id: 'search_input_sold',
                                    })}
                                </option>
                                <option key="upcoming" value="upcoming">
                                    {formatMessage({
                                        id: 'search_input_upcoming',
                                    })}
                                </option>
                            </Input>
                        </div>
                        <div className={styles.divider}>
                            <div className={styles.inner} />
                        </div>
                    </>
                )}
                <div className={classNames(styles['search-input-box'], { [styles.mobileInput]: reverseButton })}>
                    <Autosuggest
                        getSectionSuggestions={this.getSectionSuggestions}
                        getSuggestionValue={this.getSuggestionValue}
                        id={id}
                        inputProps={inputProps}
                        multiSection
                        onSuggestionsClearRequested={noop}
                        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                        renderSectionTitle={this.renderSectionTitle}
                        renderSuggestion={this.renderSuggestion}
                        shouldRenderSuggestions={this.shouldRenderSuggestions}
                        suggestions={whiteLabel ? [] : suggestions}
                    />
                    {/* Keep iconStyles classes for e2e test */}
                    <StyledSearchButton $reverseButton={reverseButton} className={iconStyles}>
                        <StyledFontAwesomeIcon
                            $reverseButton={reverseButton}
                            // Keep search-field-icon for e2e test
                            className={styles['search-field-icon']}
                            icon={faSearch}
                            onClick={this.search}
                        />
                    </StyledSearchButton>
                </div>
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SearchInput));

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{ $reverseButton?: boolean }>`
    height: 20px;
    width: 20px;
    font-size: 20px;
    color: ${({ $reverseButton, theme }) => ($reverseButton ? theme.colors.blue100 : theme.colors.white)};
`;

const StyledSearchButton = styled(Button)<{ $reverseButton?: boolean }>`
    height: 44px;
    margin: -1px -1px -1px 0;
    width: 50px;
    position: absolute;
    right: 0;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.blue100};
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.colors.blue200};
    }

    ${({ $reverseButton }) => {
        if ($reverseButton) {
            return css`
                background: none;
                border: none;

                &:hover {
                    background: none;
                }
            `;
        }
    }}
`;
